import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    Output,
    EventEmitter,
} from "@angular/core";
import { S25IconComponent } from "../modules/s25-icon/s25.icon.component";

import { S25Util } from "../util/s25-util";
import { TypeManagerDecorator } from "../main/type.map.service";
import { DomSanitizer, SafeResourceUrl, SafeHtml } from "@angular/platform-browser";
import { AttachmentService, AttachmentItem } from "../services/attachment.service";
import { S25ModalComponent } from "../modules/s25-modal/s25.modal.component";
import { S25ModalModule } from "../modules/s25-modal/s25.modal.module";

import { SpreadSheetService } from "../modules/s25-spreadsheet/spreadsheet.service";
import Spreadsheet from "../modules/s25-spreadsheet/types/x-spreadsheet.types";
import { RowData, Stox, WorkBook, XLSX } from "../modules/s25-spreadsheet/types/sheet.js.types";
import { ScrollLockService } from "../services/scroll.lock.service";
import { S25File } from "../modules/s25-file-upload/s25.file.upload.component";
import { FileStoreUtil, FileStoreParams } from "../util/filestore.util";

// mammoth .docx to HTML converter
declare global {
    interface Window {
        mammoth: any;
    }
}

@TypeManagerDecorator("s25-ng-display-file")
@Component({
    selector: "s25-ng-display-file",
    template: `
        @if (init) {
            <div class="file-wrapper">
                @if (hasRemoveButton) {
                    <s25-ng-icon [type]="'close'" [label]="removeFileName" (click)="removeFile()"></s25-ng-icon>
                }

                <s25-ng-icon [type]="getFileType()" [label]="fileName"></s25-ng-icon>

                @if (!isNewAddFile) {
                    <a target="_self" [href]="getUrl()" [attr.download]="fileName"> {{ fileName }} </a>
                } @else {
                    {{ fileName }}
                }

                @if (getFileType() === "powerPoint") {
                    <a target="_self" [href]="getUrl()" [attr.download]="fileName"> Download </a>
                } @else {
                    <span class="preview" (click)="openPreviewModal()"> Preview</span>
                }
            </div>

            <s25-ng-modal #previewModal [title]="fileName" [size]="'xl'">
                <ng-template #s25ModalBody>
                    @if (!isOfficeFile()) {
                        @if (getFileType() === "image") {
                            <div class="img-content">
                                <img [src]="safeUrl" [style.max-width]="'600px'" />
                            </div>
                        } @else {
                            <iframe [src]="safeUrl" width="100%" height="600px" frameborder="0"></iframe>
                        }
                    } @else {
                        @if (getFileType() === "excel") {
                            <div
                                [style.width]="'fit-content'"
                                class="ngHeight100 c-margin-top--single"
                                #spreadsheetEl
                            ></div>
                        } @else if (getFileType() === "text") {
                            <div class="docx-content" [innerHTML]="docxHtml"></div>
                        }
                    }
                </ng-template>
            </s25-ng-modal>
        }
    `,
    styles: [
        `
            .preview {
                cursor: pointer;
                color: #2573a7;
            }

            .docx-content {
                margin-top: 20px;
                border: 1px solid #ddd;
                padding: 15px;
                max-height: 600px;
                overflow-y: auto;
            }

            .file-wrapper {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 25px;
            }

            .img-content {
                min-height: 600px;
                display: flex;
                justify-content: center;
                align-items: center;
            }

            ::ng-deep .nm-party--on .preview,
            ::ng-deep .nm-party--on a {
                color: #73b0e2;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: true,
    imports: [S25IconComponent, S25ModalModule],
})
export class S25DisplayFileComponent implements OnInit {
    @Input() fileName?: string;
    @Input() objectType?: number;
    @Input() objectId?: number;
    @Input() hasHyperlink?: boolean;
    @Input() hasRemoveButton?: boolean = false;
    @Input() file?: AttachmentItem | S25File;
    @Input() isNewAddFile?: boolean = false;
    @Input() fileData?: any; // string | ArrayBuffer;
    /**
     * Alternative input: FileStore custom attribute value (e.g., "object_type=2&object_id=43&file_name=Pickles.jpg")
     * If provided, this will be parsed and used instead of individual fileName/objectType/objectId inputs
     */
    @Input() fileStoreUrl?: string;
    @ViewChild("previewModal") previewModal: S25ModalComponent;
    @ViewChild("spreadsheetEl") spreadsheetEl: ElementRef<HTMLDivElement>;
    @Output() onRemove = new EventEmitter();

    baseUrl: string = window.ProData && window.ProData.webservicesBaseUrl;

    static count: number = 0;
    id: string;
    init: boolean = false;
    safeUrl: SafeResourceUrl;

    spreadsheetCreated = false;
    XLSX: XLSX;
    scrollLocksHandled = false;
    spreadsheet: Spreadsheet;

    scriptsPromise?: Promise<void>;

    docxHtml: SafeHtml = "";

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private sanitizer: DomSanitizer,
    ) {
        this.elementRef.nativeElement.angBridge = this; //bridge to AngularJS; used for AngJS to set model values and call setter fns
        this.id = "ngFile-" + S25DisplayFileComponent.count++;
    }

    ngOnInit() {
        // If fileStoreUrl is provided, parse it and set fileName/objectType/objectId
        if (this.fileStoreUrl && !this.fileName) {
            const params = FileStoreUtil.parseFileStoreUrl(this.fileStoreUrl);
            if (params) {
                this.fileName = params.fileName;
                this.objectType = params.objectType;
                this.objectId = params.objectId;
            }
        }

        this.init = true;
        this.cd.detectChanges();
    }

    getUrl() {
        // https://25live.collegenet.com/25live/data/burnside/run/fs/download/download_file.json?file_name=roseflower.jpg&object_type=3&object_id=1108&display=inline
        return (
            this.baseUrl +
            "/fs/download/download_file.json?file_name=" +
            this.fileName +
            "&object_type=" +
            this.objectType +
            "&object_id=" +
            this.objectId
        );
    }

    getExtension(fileName?: string): string | undefined {
        if (!fileName || fileName === "") return;
        const parts = fileName.split(".");
        return S25Util.toStr(parts[parts.length - 1]).toLowerCase();
    }

    getFileType(): string {
        const ext = this.getExtension(this.fileName);
        return (ext && this.extensionIconMap[ext]) || "generic";
    }

    async openPreviewModal() {
        let view: Blob;

        if (this.isOfficeFile() || this.isNewAddFile) {
            if (!this.isNewAddFile) {
                this.previewModal.open(); // excel file  openModal first then create speadsheet
                view = await AttachmentService.getFile(this.objectType, this.objectId, this.fileName);
            } else {
                if (typeof this.fileData === "string" && this.fileData.startsWith("data:")) {
                    const blob = this.toBlobFromBase64(this.fileData);
                    const objectUrl = URL.createObjectURL(blob);
                    this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
                    view = blob;
                } else if (this.fileData instanceof Blob || this.fileData instanceof ArrayBuffer) {
                    const blob = new Blob([this.fileData], { type: this.getContentType() });
                    const objectUrl = URL.createObjectURL(blob);
                    this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl);
                    view = blob;
                } else {
                    console.error("Unsupported fileData type");
                }
                if (this.getFileType() !== "excel") this.previewModal.open();
            }

            if (this.getFileType() === "excel") {
                this.previewModal.open(); // excel file  openModal first then create speadsheet
                setTimeout(() => {
                    this.previewExcel(view);
                });
            } else if (this.getFileType() === "text" && this.getContentType() !== "text/plain") {
                this.previewText(view);
            }
        } else {
            const previewUrl = this.getUrl() + "&display=inline";
            this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(previewUrl);
            this.previewModal.open(); // non OfficeFile need to set safe url then openModal
        }
    }

    isOfficeFile(): boolean {
        const ext = this.getExtension(this.fileName);
        return ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext);
    }

    private extensionIconMap: { [key: string]: string } = {
        jpg: "image",
        jpeg: "image",
        png: "image",
        gif: "image",
        pdf: "pdf",
        doc: "text",
        docx: "text",
        txt: "text",
        rtf: "text",
        xls: "excel",
        xlsx: "excel",
        csv: "excel",
        ppt: "powerPoint",
        pptx: "powerPoint",
    };

    async previewExcel(view: Blob) {
        await SpreadSheetService.load();
        this.XLSX = await SpreadSheetService.XLSX();
        this.spreadsheet = window.x_spreadsheet(this.spreadsheetEl.nativeElement, {
            mode: "edit",
            showToolbar: false,
            showGrid: true,
            showContextmenu: true,
            showBottomBar: false,
            view: {
                height: () => 500,
                width: () => 800,
            },
        });
        const arrayBuffer = await view.arrayBuffer();
        const workbook = this.XLSX.read(arrayBuffer);
        await this.processWorkbook(workbook);
    }

    async previewText(view: Blob) {
        await this.loadScripts();
        const arrayBuffer = await view.arrayBuffer();
        const result = await window.mammoth.convertToHtml({ arrayBuffer });
        this.docxHtml = this.sanitizer.bypassSecurityTrustHtml(result.value);
        this.cd.detectChanges();
    }

    getContentType() {
        if (!this.fileData) return undefined;
        const parts = this.fileData.split(",");
        const mime = parts[0].match(/:(.*?);/)?.[1] ?? "";
        return mime;
    }

    processWorkbook = async (workbook: WorkBook) => {
        let xsprData: Stox = await SpreadSheetService.stox(workbook);
        this.spreadsheet.loadData(xsprData);
        this.getSheetData();
        this.cd.detectChanges();
    };

    getSheetData = (): RowData[] => {
        if (!this.spreadsheet) {
            return [];
        }
        let rows: RowData[] = [];
        let data = this.spreadsheet.getData();
        if (!data || !data.length || !data[0]) {
            return rows;
        }
        let sheet = data[0];
        if (sheet.rows) {
            for (let rowNum in sheet.rows) {
                let row = sheet.rows[rowNum];
                let rowData: RowData = { cells: [] };
                let cols = 0;
                for (let colNum in row.cells) {
                    let cell = row.cells[colNum];
                    cols++;
                    rowData.cells.push({ text: S25Util.toStr(cell?.text) });
                }
                if (cols <= 0) {
                    break;
                }
                rows.push(rowData);
            }
        }
        return rows;
    };

    handleScrollLock = () => {
        if (this.scrollLocksHandled) {
            return;
        }
        this.scrollLocksHandled = true;

        this.spreadsheetEl.nativeElement.addEventListener("mouseover", () => {
            ScrollLockService.lockBodyScroll();
        });

        this.spreadsheetEl.nativeElement.addEventListener("mouseout", (evt) => {
            if (!this.isEventOverlappingElement(evt, this.spreadsheetEl.nativeElement)) {
                ScrollLockService.unlockBodyScroll();
            }
        });
        document.addEventListener("wheel", this.wheelListener, { passive: false });
    };

    wheelListener = (event: WheelEvent) => {
        if ((event.target as HTMLElement)?.classList?.contains("x-spreadsheet-overlayer")) {
            if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
                event.preventDefault();

                const currentScroll = this.spreadsheet.sheet.horizontalScrollbar.scroll();
                let newLeftPosition = currentScroll.left + event.deltaX;

                this.spreadsheet.sheet.horizontalScrollbar.moveFn(newLeftPosition);
                this.spreadsheet.sheet.horizontalScrollbar.move({ left: newLeftPosition });
            }
        }
    };

    isEventOverlappingElement = (event: MouseEvent, element: HTMLElement) => {
        const rect = element.getBoundingClientRect();
        return (
            event.clientX >= rect.left &&
            event.clientX <= rect.right &&
            event.clientY >= rect.top &&
            event.clientY <= rect.bottom
        );
    };

    async loadScripts(): Promise<void> {
        this.scriptsPromise =
            this.scriptsPromise ||
            S25Util.all([]).then(async () => {
                await S25Util.loadScript("docx-word-js", "https://unpkg.com/mammoth/mammoth.browser.min.js");
                return;
            });
        return this.scriptsPromise;
    }

    async removeFile() {
        try {
            if (this.fileName && this.fileName !== "") {
                this.onRemove.emit(this.file);
            }
        } catch (err) {
            S25Util.showError(err);
        }
        this.cd.detectChanges();
    }

    toBlobFromBase64(base64Data: string): Blob {
        const parts = base64Data.split(",");
        if (parts.length !== 2) {
            throw new Error("Invalid base64 data format");
        }
        const mime = parts[0].match(/:(.*?);/)?.[1] || "application/octet-stream";
        const binaryString = atob(parts[1]);
        const byteArray = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            byteArray[i] = binaryString.charCodeAt(i);
        }
        return new Blob([byteArray], { type: mime });
    }
}
